import { useLazyQuery } from "@apollo/client"
import { Dialog, Transition } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/20/solid"
import twcn from "classnames"
import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type RegisterOptions,
  type Resolver,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form"
import FormForm from "@/admin/components/form/form"
import theme from "@/admin/theme"
import { graphql } from "@/api"
import { type AdditionalProductsQuery, type AdminOrderArticlesQuery } from "@/api/graphql"
import DotSpinner from "@/common/components/dot_spinner"
import { dig, isBlank } from "@/common/util"

const predefinedDoc = graphql(`
  query AdditionalProducts($locale: String) {
    additionalProducts(locale: $locale) {
      name
      key
      type
      price
    }
  }
`)

type Item = AdminOrderArticlesQuery["order"]["orderItems"][0]

const materials = [
  "Select material",
  "Acrylic",
  "Acrylic and aluminium backplate",
  "Aluminum Anodized (laser etching)",
  "Aluminum Lacquered",
  "Aluminum white",
  "Banner",
  "Brass",
  "Eco board",
  "Forex",
  "Gobond Alu/alu",
  "Gobond Alu/white",
  "Magnetic",
  "Plastic engrave",
  "Poster",
  "Reflective",
  "Rollup regular",
  "Rollup eco",
  "Solid PET",
  "Stainless steel",
  "Vinyl (Decal)",
  "Vinyl (Vinyl lettering)",
  "Wood (MDF)",
  "Wood (Solid)",
  "Custom",
]

const bgColors = [
  "Select color",
  "Aluminum",
  "Black",
  "Blue",
  "Brown",
  "Cherry",
  "Dark brown",
  "Frosted",
  "Gold",
  "Gray",
  "Green",
  "Maple",
  "Oak",
  "Orange",
  "Pink",
  "Purple",
  "Red",
  "Rose gold",
  "Silver",
  "Transparent",
  "White",
  "Yellow",
  "Black-Matte",
  "Blue-Matte",
  "Red-Matte",
  "Gold-Glossy",
  "Custom",
]

type Product = AdditionalProductsQuery["additionalProducts"][0]

export type CustomProductAttributes = Record<string, string>

interface AdditionalProductInputs {
  custom: {
    name?: string
    count?: string
    width?: string
    height?: string
    thickness?: string
    customProductAttributes?: {
      color?: string
      color_custom?: string
      fixmethod?: string
      fixmethod_count?: string
      fixmethod_custom?: string
      material?: string
      material_custom?: string
    }
  }
}

interface CellInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: FieldPath<AdditionalProductInputs>
  rules?: RegisterOptions<AdditionalProductInputs>
}

export const fixmethodTranslationKey = (key: string) => {
  if (key.includes("screw_capped")) {
    return "screw_capped"
  }
  if (key.includes("screw") && !key.includes("tape_and_screw")) {
    return "screw"
  }
  if (key.includes("magnet_rug")) {
    return "magnet_rug"
  }
  if (key.includes("magnet")) {
    return "magnet"
  }
  if (key.includes("eyelet")) {
    return "eyelets"
  }
  if (key.includes("rollup")) {
    return "stand"
  }
  if (key.includes("flag_mount")) {
    return "flag_mount"
  }
  if (key.includes("pole")) {
    return "pole"
  }
  if (key.includes("wall_distance")) {
    return "wall_distance"
  }
  if (key.includes("ceiling_mount")) {
    return "ceiling_mount"
  }
  if (key.includes("foam_tape")) {
    return "tape"
  }

  return key
}

const CellInput: React.FC<CellInputProps> = ({ name, rules, ...props }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<AdditionalProductInputs>()

  const error = dig(errors, name) as FieldError | undefined
  const registerFields = register(name, rules)

  const className = twcn(
    "z-10 block w-full flex-1 appearance-none border-0 bg-white px-2 py-[0.375rem] leading-5 placeholder-gray-500 focus:placeholder-gray-400 focus:outline-none focus:ring-2 dark:bg-gray-900 dark:text-gray-200 dark:placeholder-gray-400 sm:text-sm",
    {
      "text-red-900 placeholder-red-300 ring-2 ring-red-500 dark:placeholder-red-700": error,
      "focus:ring-blue-600": !error,
    }
  )

  return (
    <td className="whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
      <div className="p-px">
        <input {...registerFields} className={className} {...props} type={props.type || "text"} />
      </div>
    </td>
  )
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name: FieldPath<AdditionalProductInputs>
  options: Product[] | string[] | any
  className?: string
  rules?: RegisterOptions<AdditionalProductInputs>
  id?: string
  colorCustom?: boolean
  materialCustom?: boolean
  fixingCustom?: boolean
}

const Select: React.FC<SelectProps> = ({
  name,
  options,
  rules,
  colorCustom,
  materialCustom,
  fixingCustom,
  ...props
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<AdditionalProductInputs>()
  const error = dig(errors, name) as FieldError | undefined
  const registerFields = register(name, rules)
  const defaultValue = () => {
    if (
      (name === "custom.customProductAttributes.color" && colorCustom) ||
      (name === "custom.customProductAttributes.material" && materialCustom) ||
      (name === "custom.customProductAttributes.fixmethod" && fixingCustom)
    ) {
      return "Custom"
    }

    return
  }

  const className = twcn(
    "z-10 block w-full flex-1 appearance-none border-0 bg-white px-2 py-[0.375rem] leading-5 placeholder-gray-500 focus:placeholder-gray-400 focus:outline-none focus:ring-2 dark:bg-gray-900 dark:text-gray-200 dark:placeholder-gray-400 sm:text-sm",
    {
      "text-red-900 placeholder-red-300 ring-2 ring-red-500 dark:placeholder-red-700": error,
      "focus:ring-blue-600": !error,
    }
  )

  if (name === "custom.customProductAttributes.fixmethod") {
    options.unshift("Select fixing method")
    options.push({ key: "hanging", name: "Hanging", type: "fixmethod" })
    options.push({ key: "tape_and_screw", name: "Tape and screw", type: "fixmethod" })
    options.push({ key: "self_adhesive", name: "Self adhesive", type: "fixmethod" })
    options.push({ key: "self_adhesive_floor", name: "Self adhesive floor", type: "fixmethod" })
    options.push({ key: "none", name: "None", type: "fixmethod" })
    options.push("Custom")
  }

  return (
    <td className="whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
      <div className="relative p-px">
        <ChevronDownIcon className="pointer-events-none absolute right-2 top-2 h-5 w-5" />
        <select {...registerFields} className={`${className} cursor-pointer`} {...props} value={defaultValue()}>
          {options.map((option: any, i: number) => {
            if (["fixmethod", "tape", "screw", "other"].includes(option?.type)) {
              return (
                <option key={option.key} value={i === 0 ? "" : option.key}>
                  {option.name}
                </option>
              )
            } else {
              return (
                <option key={option} value={i === 0 ? "" : option}>
                  {option}
                </option>
              )
            }
          })}
        </select>
      </div>
    </td>
  )
}

interface CustomFixingMethodBlockProps {
  title: string
  products: Product[]
  setFixingCustom: (fixingCustom: boolean) => void
  fixingCustom: boolean
}

const CustomFixingMethodBlock: React.FC<CustomFixingMethodBlockProps> = ({
  title,
  products,
  setFixingCustom,
  fixingCustom,
}) => {
  const handleCustomFixing = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length >= 1) {
      setFixingCustom(true)
    } else {
      setFixingCustom(false)
    }
  }

  return (
    <div>
      <div className="mb-2 text-xs uppercase text-gray-700 dark:text-gray-400">{title}</div>
      <div className="mt-4 overflow-hidden rounded bg-white shadow-sm ring-1 ring-slate-700/10 dark:bg-ds9-900/50 dark:ring-slate-600/40">
        <table className="min-w-full table-fixed">
          <colgroup>
            <col width="45%" />
            <col width="40%" />
            <col width="15%" />
          </colgroup>
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
            <tr className="divide-x divide-gray-200">
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Type
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Custom fixing
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                QTY EA.
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900/20">
            <tr className="divide-x divide-gray-200">
              <Select
                name="custom.customProductAttributes.fixmethod"
                options={products}
                fixingCustom={fixingCustom}
                id={"fixmethod"}
              />
              <CellInput
                name={`custom.customProductAttributes.fixmethod_custom`}
                rules={{ onChange: e => handleCustomFixing(e) }}
                id={"fixmethod-custom"}
              />
              <CellInput name={`custom.customProductAttributes.fixmethod_count`} type="number" min="1" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface CustomAppearanceBlockProps {
  title: string
  setColorCustom: (colorCustom: boolean) => void
  setMaterialCustom: (materialCustom: boolean) => void
  colorCustom: boolean
  materialCustom: boolean
}

const CustomAppearanceBlock: React.FC<CustomAppearanceBlockProps> = ({
  title,
  setColorCustom,
  setMaterialCustom,
  colorCustom,
  materialCustom,
}) => {
  const handleCustomMaterial = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length >= 1) {
      setMaterialCustom(true)
    } else {
      setMaterialCustom(false)
    }
  }

  const handleCustomColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length >= 1) {
      setColorCustom(true)
    } else {
      setColorCustom(false)
    }
  }

  return (
    <div>
      <div className="mb-2 text-xs uppercase text-gray-700 dark:text-gray-400">{title}</div>
      <div className="mt-4 overflow-hidden rounded bg-white shadow-sm ring-1 ring-slate-700/10 dark:bg-ds9-900/50 dark:ring-slate-600/40">
        <table className="min-w-full table-fixed">
          <colgroup>
            <col width="25%" />
            <col width="25%" />
            <col width="25%" />
            <col width="25%" />
          </colgroup>
          <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
            <tr className="divide-x divide-gray-200">
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Material
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Custom material
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Color
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                Custom color
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900/20">
            <tr className="divide-x divide-gray-200">
              <Select
                name={`custom.customProductAttributes.material`}
                options={materials}
                materialCustom={materialCustom}
                id={"material"}
              />
              <CellInput
                name={`custom.customProductAttributes.material_custom`}
                rules={{ onChange: e => handleCustomMaterial(e) }}
                id={"material-custom"}
              />
              <Select
                name={`custom.customProductAttributes.color`}
                options={bgColors}
                colorCustom={colorCustom}
                id={"color"}
              />
              <CellInput
                name={`custom.customProductAttributes.color_custom`}
                rules={{ onChange: e => handleCustomColor(e) }}
                id={"color-custom"}
              />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

const formResolver: Resolver<AdditionalProductInputs> = async values => {
  const custom = values.custom || {}
  const errors: FieldErrors<AdditionalProductInputs> = {}

  if (isBlank(custom.name) || isBlank(custom.count)) {
    if (isBlank(custom.name)) {
      errors.custom = errors.custom || {}
      errors.custom.name = { type: "required" }
    }

    if (isBlank(custom.count)) {
      errors.custom = errors.custom || {}
      errors.custom.count = { type: "required" }
    }
  }

  return {
    values,
    errors,
  }
}

export interface CustomProductSubmit {
  name: string
  key: string
  count: number
  price?: string
  width?: string
  height?: string
  thickness?: string
  customProductAttributes?: CustomProductAttributes
}

interface CustomProductsModalProps {
  item?: Item
  locale?: string | null
  open: boolean
  setOpen: (open: boolean) => void
  submit: (products: CustomProductSubmit[]) => void
}

const CustomProductsModal: React.FC<CustomProductsModalProps> = ({ open, setOpen, locale, submit, item }) => {
  const [load, results] = useLazyQuery(predefinedDoc, { fetchPolicy: "cache-and-network", variables: { locale } })
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const [colorCustom, setColorCustom] = useState(false)
  const [materialCustom, setMaterialCustom] = useState(false)
  const [fixingCustom, setFixingCustom] = useState(false)

  useEffect(() => {
    if (open) {
      load()
    }
  }, [open, load])

  const predefined = results.data?.additionalProducts || []
  const fixmethod = predefined.filter(item => ["fixmethod", "tape", "screw", "other"].includes(item.type))
  const customFixmethod = !isBlank(item?.customProductAttributes?.fixmethod_custom)
  const customColor = !isBlank(item?.customProductAttributes?.color_custom)
  const customMaterial = !isBlank(item?.customProductAttributes?.material_custom)

  const formMethods = useForm<AdditionalProductInputs>({
    resolver: formResolver,
    defaultValues: {
      custom: {
        name: item?.description || "",
        count: item?.count.toString() || "",
        width: item?.widthDisplay?.toString() || "",
        height: item?.heightDisplay?.toString() || "",
        thickness: item?.thickness?.toString() || "",
        customProductAttributes: {
          color: customColor ? "Custom" : item?.customProductAttributes?.color || "",
          color_custom: item?.customProductAttributes?.color_custom || "",
          fixmethod: customFixmethod ? "Custom" : item?.customProductAttributes?.fixmethod || "",
          fixmethod_count: item?.customProductAttributes?.fixmethod_count || "",
          fixmethod_custom: item?.customProductAttributes?.fixmethod_custom || "",
          material: customMaterial ? "Custom" : item?.customProductAttributes?.material || "",
          material_custom: item?.customProductAttributes?.material_custom,
        },
      },
    },
  })
  const { handleSubmit, reset } = formMethods

  const close = useCallback(() => {
    setOpen(false)
    setFixingCustom(false)
    setMaterialCustom(false)
    setColorCustom(false)
    reset()
  }, [setOpen, reset])

  const onSubmit: SubmitHandler<AdditionalProductInputs> = useCallback(
    data => {
      const products: CustomProductSubmit[] = []

      if (!isBlank(data.custom?.name)) {
        const customProductAttributes = data.custom.customProductAttributes

        if (customProductAttributes) {
          if ((customProductAttributes.material_custom || "").length > 0) {
            customProductAttributes.material = ""
          }

          if ((customProductAttributes.color_custom || "").length > 0) {
            customProductAttributes.color = ""
          }

          if ((customProductAttributes.fixmethod_custom || "").length > 0) {
            customProductAttributes.fixmethod = ""
          }
        }

        products.push({
          key: "custom_product",
          name: data.custom.name || "",
          count: parseInt(data.custom.count || "") || 0,
          width: data.custom.width?.replace(",", ".") || "",
          height: data.custom.height?.replace(",", ".") || "",
          thickness: data.custom.thickness?.replace(",", ".") || "",
          customProductAttributes: customProductAttributes,
        })
      }

      submit(products)
      close()
    },
    [close, submit]
  )

  return (
    <Transition.Root show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-10" onClose={close}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity dark:bg-black dark:bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="custom-product-modal relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all dark:bg-gray-800 sm:mx-8 sm:my-8 sm:max-w-7xl">
                <FormForm onSubmit={handleSubmit(onSubmit, e => console.log(e))} formMethods={formMethods}>
                  <h3 className="mb-1 py-4 text-center text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                    Add Custom Product
                  </h3>
                  <div>
                    <div className="bg-gray-200 px-8 py-4 shadow-inner dark:bg-gray-900">
                      {results.loading && <DotSpinner center />}
                      {!results.loading && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-3 grid grid-cols-3 content-start gap-4">
                            <div className="col-span-2">
                              <div className="mb-2 text-xs uppercase text-gray-700 dark:text-gray-400">
                                Custom product
                              </div>
                              <div className="overflow-hidden rounded bg-white shadow-sm ring-1 ring-slate-700/10 dark:bg-ds9-900/50 dark:ring-slate-600/40">
                                <table className="min-w-full table-fixed">
                                  <colgroup>
                                    <col width="80%" />
                                    <col width="20%" />
                                  </colgroup>
                                  <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
                                    <tr className="divide-x divide-gray-200">
                                      <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                        Name
                                      </th>
                                      <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                        QTY
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900/20">
                                    <tr className="divide-x divide-gray-200">
                                      <CellInput name="custom.name" defaultValue={item?.description || ""} />
                                      <CellInput name="custom.count" type="number" min="1" defaultValue={item?.count} />
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <div className="col-span-3 grid grid-cols-2 content-start gap-4">
                              <div className="space-y-4">
                                <div className="mb-2 text-xs uppercase text-gray-700 dark:text-gray-400">
                                  Dimensions (mm)
                                </div>
                                <div className="overflow-hidden rounded bg-white shadow-sm ring-1 ring-slate-700/10 dark:bg-ds9-900/50 dark:ring-slate-600/40">
                                  <table className="min-w-full table-fixed">
                                    <colgroup>
                                      <col width="33%" />
                                      <col width="33%" />
                                      <col width="33%" />
                                    </colgroup>
                                    <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
                                      <tr className="divide-x divide-gray-200">
                                        <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                          Width
                                        </th>
                                        <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                          Height
                                        </th>
                                        <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                          Thickness
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900/20">
                                      <tr className="divide-x divide-gray-200">
                                        <CellInput name="custom.width" />
                                        <CellInput name="custom.height" />
                                        <CellInput name="custom.thickness" />
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                              <div className="space-y-4">
                                <CustomFixingMethodBlock
                                  title={"Fixing Method"}
                                  products={fixmethod}
                                  setFixingCustom={setFixingCustom}
                                  fixingCustom={fixingCustom}
                                />
                              </div>
                            </div>
                            <div className="col-span-3">
                              <div className="space-y-4">
                                <CustomAppearanceBlock
                                  title={"Appearance"}
                                  setColorCustom={setColorCustom}
                                  setMaterialCustom={setMaterialCustom}
                                  colorCustom={colorCustom}
                                  materialCustom={materialCustom}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 px-6 py-3">
                    <button className={twcn(theme.button("ghost"))} onClick={close} ref={cancelButtonRef} type="button">
                      Cancel
                    </button>
                    <button
                      className={twcn(theme.button("primary"))}
                      type="submit"
                      disabled={results.loading || !formMethods.formState.isValid}
                    >
                      {item ? "Add changes" : "Add"}
                    </button>
                  </div>
                </FormForm>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default CustomProductsModal
